import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "../components/ui/navigation-menu"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { useResolveCls } from "../contexts/breakpoint-context"

export function NavigationMenuRenderer({ element }: RendererProps) {
  const { resolveSymbol, navigate } = useRenderContext()
  const rcls = useResolveCls()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: child items use `variant` as a route-carrier (see
  // `const route = child.variant ?? child.href ?? ""` below). That legacy
  // overload applies to children only; the outer NavigationMenu's atom
  // contract flows from the element's own variant/size and atom defaults.
  const atomDefaults = useAtomDefaults("navigation")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const items = element.children ?? []
  const currentRoute = String(
    resolveSymbol("#{_system.navigation.currentRoute}") ?? "/",
  )

  const handleNavigate = (route: string) => {
    if (route && navigate) {
      navigate(route)
    }
  }

  return (
    <NavigationMenu
      className={cn(element.className)}
      data-testid={`navigation-menu-${element.id}`}
      data-atom-type="navigation"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <NavigationMenuList className="gap-1">
        {items.map((child, index) => {
          const label = child.text ? String(resolveSymbol(child.text) ?? "") : ""
          const route = child.variant ?? child.href ?? ""

          // Active state: exact match or prefix match for nested routes
          const isActive = route
            ? currentRoute === route ||
              (route !== "/" && currentRoute.startsWith(route))
            : false

          const activeClass = child.activeClassName ?? element.activeClassName
          const inactiveClass = child.inactiveClassName ?? element.inactiveClassName

          return (
            <NavigationMenuItem key={`${element.id}-${index}-${child.id}`}>
              <NavigationMenuLink
                render={<button type="button" />}
                className={cn(
                  "rounded-md px-3 py-2 text-sm font-medium",
                  isActive
                    ? activeClass ?? "bg-accent text-accent-foreground font-semibold"
                    : inactiveClass ?? "text-muted-foreground",
                  rcls(child.className),
                )}
                active={isActive}
                data-active={isActive || undefined}
                data-route={route || undefined}
                onClick={() => handleNavigate(route)}
              >
                {label}
              </NavigationMenuLink>
            </NavigationMenuItem>
          )
        })}
      </NavigationMenuList>
    </NavigationMenu>
  )
}
